/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
*
* NVIDIA CORPORATION and its licensors retain all intellectual property
* and proprietary rights in and to this software, related documentation
* and any modifications thereto.  Any use, reproduction, disclosure or
* distribution of this software and related documentation without an express
* license agreement from NVIDIA CORPORATION is strictly prohibited.
*/

/** This file was automatically generated. DO NOT MODIFY IT BY HAND. */

/* eslint-disable @typescript-eslint/naming-convention */

export module DownloaderTypes {

/** This object was automatically generated by json-schema-to-typescript. DO NOT MODIFY IT BY HAND. */

export interface Downloader {
    [k: string]: any;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "DownloadTypeInfo".
 */
export interface DownloadTypeInfo {
    /**
     * Name of the download component
     */
    componentName: string;
    updateChannel: UpdateChannel;
    updateType: UpdateType;
    invokeType: InvokeType;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "DownloadProgressStatus".
 */
export interface DownloadProgressStatus {
    downloadStatus: DownloadStatus;
    /**
     * Percentage to which the download is completed
     */
    percentComplete: number;
    /**
     * time remaining for download complete
     */
    timeRemaining: number;
    /**
     * speed of the download
     */
    downloadSpeed: number;
    /**
     * total size of the download, in bytes
     */
    totalBytes: number;
    /**
     * bytes that are downloaded so far
     */
    bytesDone: number;
    /**
     * Id of the download task
     */
    taskId: string;
    /**
     * path of the downloaded file
     */
    downloadedLocation: string;
    /**
     * number of times the download failed
     */
    failureCount: number;
    /**
     * version of the component being downloaded
     */
    version: string;
    downloadTypeInfo: DownloadTypeInfo;
    /**
     * http response code
     */
    httpResponseCode: number;
    /**
     * error code from downloader
     */
    errorCode: string;
    /**
     * whether the download was paused once or not
     */
    downloadPausedOnce: number;
    /**
     * url of the download
     */
    downloadUrl: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "DownloadProgressStatusReturn".
 */
export interface DownloadProgressStatusReturn {
    updateFrameworkResult: UpdateFrameworkResult;
    downloadProgressStatus: DownloadProgressStatus;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "PostProcessingAction".
 */
export interface PostProcessingAction {
    /**
     * name of the action
     */
    name: string;
    /**
     * output of the action. the string can contain anything right from XML,
     * JSON to simple path string, if there is no output it'll be empty
     */
    output?: string;
    actionStatus: ActionStatus;
    /**
     * count of the retried attempts for this action, after failure
     */
    retryCount: number;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "PostProcessingStatusInfo".
 */
export interface PostProcessingStatusInfo {
    /**
     * Id of the task for which status is to be queried
     */
    taskId: string;
    postProcessingStatus: PostProcessingStatus;
    actions: PostProcessingAction[];
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "PostProgressStatusReturn".
 */
export interface PostProgressStatusReturn {
    updateFrameworkResult: UpdateFrameworkResult;
    postProcessingStatusInfo: PostProcessingStatusInfo;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "CheckForUpdateStatus".
 */
export interface CheckForUpdateStatus {
    /**
     * Boolean describing whether beta was requested
     */
    isBetaRequested: boolean;
    progressStatus: ProgressStatus;
    /**
     * url of the download
     */
    url: string;
    /**
     * whether the update that is available is beta or not
     */
    isBeta: boolean;
    /**
     * version of the available update
     */
    version: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "AutoUpdateProgressInfo".
 */
export interface AutoUpdateProgressInfo {
    /**
     * Name of the component to auto update
     */
    componentName: string;
    /**
     * Id of the task for which status is to be reported
     */
    taskId?: string;
    updateState: UpdateState;
    updateExecutionPhase: UpdateExecutionPhase;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "AutoUpdateStatusReturn".
 */
export interface AutoUpdateStatusReturn {
    updateFrameworkResult: UpdateFrameworkResult;
    autoUpdateProgressInfo: AutoUpdateProgressInfo;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "GetFirstBootTimeAfterInstallReturn".
 */
export interface GetFirstBootTimeAfterInstallReturn {
    updateFrameworkResult: UpdateFrameworkResult;
    /**
     * Time of the first boot after install. ex. Tue Aug  8 12:38:54 2023
     */
    firstBootTime: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "GetMaxDaysBetweenReleasesParameters".
 */
export interface GetMaxDaysBetweenReleasesParameters {
    /**
     * Name of the component for which max days between releases is to be fetched
     */
    componentName: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "GetMaxDaysBetweenReleasesReturn".
 */
export interface GetMaxDaysBetweenReleasesReturn {
    updateFrameworkResult: UpdateFrameworkResult;
    /**
     * this is configured in profile catalog
     */
    maxDaysBetweenReleases: number;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "CheckForUpdateParameters".
 */
export interface CheckForUpdateParameters {
    downloadTypeInfo: DownloadTypeInfo;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "CheckForUpdateReturn".
 */
export interface CheckForUpdateReturn {
    updateFrameworkResult: UpdateFrameworkResult;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "GetLatestUpdateCheckTimeParameters".
 */
export interface GetLatestUpdateCheckTimeParameters {
    /**
     * Name of the component for which latest update check information is to be fetched
     */
    componentName: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "GetLatestUpdateCheckTimeReturn".
 */
export interface GetLatestUpdateCheckTimeReturn {
    updateFrameworkResult: UpdateFrameworkResult;
    /**
     * Time of the latest successfull update check time. ex. Tue Aug  8 12:38:54 2023
     */
    latestUpdatecheckTime: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "GetTaskDownloadStatusParameters".
 */
export interface GetTaskDownloadStatusParameters {
    /**
     * Name of the component for which download status is to be fetched
     */
    componentName: string;
    /**
     * Id of the task for which status is to be queried
     */
    taskId: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "GetDownloadStatusParameters".
 */
export interface GetDownloadStatusParameters {
    /**
     * Name of the component for which download status is to be fetched
     */
    componentName: string;
    /**
     * version of the component being downloaded
     */
    version: string;
    /**
     * url of the component being downloaded
     */
    url: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "StartDownloadParameters".
 */
export interface StartDownloadParameters {
    /**
     * version of the component to be downloaded
     */
    version: string;
    /**
     * url of the download
     */
    url: string;
    downloadTypeInfo: DownloadTypeInfo;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "StartDownloadAndPostProcessParameters".
 */
export interface StartDownloadAndPostProcessParameters {
    /**
     * version of the component to be downloaded
     */
    version: string;
    /**
     * url of the download
     */
    url: string;
    downloadTypeInfo: DownloadTypeInfo;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "ScheduleDownloadAndPostProcessParameters".
 */
export interface ScheduleDownloadAndPostProcessParameters {
    /**
     * Number of seconds after the download and post processing is to be scheduled
     */
    scheduleAfterSeconds: number;
    /**
     * version of the component to be downloaded and extracted
     */
    version: string;
    /**
     * url of the download
     */
    url: string;
    downloadTypeInfo: DownloadTypeInfo;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "PauseDownloadParameters".
 */
export interface PauseDownloadParameters {
    /**
     * Name of the component for which download needs to be paused
     */
    componentName: string;
    /**
     * Id of the download task which needs to be paused. Note post processing cannot be paused.
     */
    taskId: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "ResumeDownloadParameters".
 */
export interface ResumeDownloadParameters {
    /**
     * Name of the component for which download & post processing is to be resumed
     */
    componentName: string;
    /**
     * Id of the download task which needs to be resumed
     */
    taskId: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "ResumeDownloadAndPostProcessParameters".
 */
export interface ResumeDownloadAndPostProcessParameters {
    /**
     * Name of the component for which download & post processing is to be resumed
     */
    componentName: string;
    /**
     * Id of the download task which needs to be resumed
     */
    taskId: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "StopDownloadParameters".
 */
export interface StopDownloadParameters {
    /**
     * Name of the component for which download needs to be stopped
     */
    componentName: string;
    /**
     * Id of the download task which needs to be stopped
     */
    taskId: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "GetPostProcessingStatusParameters".
 */
export interface GetPostProcessingStatusParameters {
    /**
     * Name of the component for which post processing needs to be started
     */
    componentName: string;
    /**
     * Id of the download task for which post processing status is queried
     */
    taskId: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "StartPostProcessingParameters".
 */
export interface StartPostProcessingParameters {
    /**
     * Name of the component for which post processing needs to be started
     */
    componentName: string;
    /**
     * Id of the download task for which post processing needs to be started
     */
    taskId: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "TriggerAutoUpdateParameters".
 */
export interface TriggerAutoUpdateParameters {
    /**
     * Name of the component for which auto update is to be triggered
     */
    componentName: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "TriggerAutoUpdateReturn".
 */
export interface TriggerAutoUpdateReturn {
    updateFrameworkResult: UpdateFrameworkResult;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "GetAutoUpdateStatusParameters".
 */
export interface GetAutoUpdateStatusParameters {
    /**
     * Name of the component for which auto update status is to be fetched
     */
    componentName: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "CreateProfileFromSourceProfileParameters".
 */
export interface CreateProfileFromSourceProfileParameters {
    /**
     * Name of the component to copy profile from
     */
    componentName: string;
    /**
     * Name of the component to copy profile from
     */
    sourceComponentName: string;
    /**
     * Update profile if already exists
     */
    forceUpdate?: boolean;
    /**
     * Type of checksum to verify. NONE,MD5,SHA512,SHA256
     */
    checksumType?: string;
    /**
     * Registry path used to detect installation state
     */
    installationKey?: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "CreateProfileFromSourceProfileReturn".
 */
export interface CreateProfileFromSourceProfileReturn {
    updateFrameworkResult: UpdateFrameworkResult;
    createProfileStatus: CreateProfileStatusEnum;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "GetUpdateChannelParameters".
 */
export interface GetUpdateChannelParameters {
    /**
     * Name of the component
     */
    componentName: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "GetUpdateChannelReturn".
 */
export interface GetUpdateChannelReturn {
    updateFrameworkResult: UpdateFrameworkResult;
    /**
     * Name of the channel
     */
    updateChannel: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "SetUpdateChannelParameters".
 */
export interface SetUpdateChannelParameters {
    /**
     * Name of the component
     */
    componentName: string;
    /**
     * Name of the channel
     */
    updateChannel: string;
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "SetUpdateChannelReturn".
 */
export interface SetUpdateChannelReturn {
    updateFrameworkResult: UpdateFrameworkResult;
}

/**
 * Enum for error code returned by update framework
 *
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "UpdateFrameworkResult".
 */
export enum UpdateFrameworkResult {
    NotImplemented = -1,
    Success = 0,
    Error = 1,
    UpdateFrameworkUninitialised = 2,
    UpdateCheckMgrUninitialised = 3,
    NoUpdateCheckCandidate = 4,
    InvalidComponentType = 5,
    UpdateCheckInfoException = 6,
    UpdateCheckComponentInapplicable = 7,
    InvalidArgument = 8,
    InvalidOperation = 9,
    FileDoesNotExist = 10,
    NotFound = 11,
    BadJson = 12,
    KeyDoesNotExist = 13,
    InternalException = 14,
    ProfileCatalogUninitialised = 15,
    AlreadyDownloaded = 16,
    CannotDeleteFile = 17,
    DownloaderBusyTryLater = 18,
    CurlInitFailed = 19,
    PostProcessingPolicyDontRun = 20,
    DownloadPolicyDontRun = 21,
    UpdateCheckPeriodNotElapsed = 22,
    PluginLoadError = 23,
    PluginFnAddrError = 24,
    PluginError = 25
}
/**
 * Enum channel of the update
 *
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "UpdateChannel".
 */
export enum UpdateChannel {
    Official = 0,
    Beta = 1,
    Unknown = -1
}
/**
 * Enum type of the update
 */
export enum UpdateType {
    Full = 0,
    Diff = 1,
    Unknown = -1
}
/**
 * Enum type of the update invocation
 */
export enum InvokeType {
    Auto = 0,
    Manual = 1,
    Unknown = -1
}
/**
 * Enum defining download status
 */
export enum DownloadStatus {
    Undefined = -1,
    DownloadTriggered = 0,
    Downloading = 1,
    VerifyingChecksum = 2,
    VerifyingSignature = 3,
    Paused = 4,
    Cancelled = 5,
    NetworkErrorRetrying = 6,
    NetworkErrorRetryPaused = 7,
    Finished = 8,
    ChecksumVerificationFailed = 9,
    SignatureVerificationFailed = 10,
    DiskWriteFail = 11,
    Error = 12,
    NotAllowedNow = 13,
    Prohibited = 14
}
/**
 * enum for status of the action
 */
export enum ActionStatus {
    Undefined = -1,
    Ready = 0,
    Executing = 1,
    Completed = 2,
    CouldNotRun = 3,
    FailedRetryPending = 4,
    Failed = 5
}
/**
 * Enum defining post processing status
 */
export enum PostProcessingStatus {
    Undefined = -1,
    ExecutingActions = 0,
    Completed = 1,
    Failed = 2
}
/**
 * Enum defining return code of the update check
 */
export enum ProgressStatus {
    Started = 0,
    Retrying = 1,
    RetryStopped = 2,
    Finished = 3,
    Failed = 4
}
/**
 * Enum defining update state
 */
export enum UpdateState {
    Unknown = -1,
    UpdateInProgress = 0,
    UpdateUnavailable = 1,
    UpdateSuccessful = 2,
    UpdateFailed = 3
}
/**
 * Enum defining update execution phase
 */
export enum UpdateExecutionPhase {
    Uninitialised = -1,
    CheckingForUpdate = 0,
    UpdateCheckEnd = 1,
    Downloading = 2,
    DownloadEnd = 3,
    PostProcessing = 4,
    PostProcessingEnd = 5
}
/**
 * This interface was referenced by `Downloader`'s JSON-Schema
 * via the `definition` "CreateProfileStatusEnum".
 */
export enum CreateProfileStatusEnum {
    Success = 0,
    Failure = 1,
    FailureNoSourceProfile = 2
}


}
